import {
    addWeeks,
    endOfMonth,
    set,
    getMonth,
    startOfMonth,
    startOfDay,
    endOfDay,
    getYear,
    isValid,
} from "date-fns"
import React, {
    useState,
    createContext,
    useContext,
    useMemo,
    type PropsWithChildren,
} from "react"
import { useDisclosure } from "@mallardbay/lib-react-components"
import { Views, type View } from "react-big-calendar"

import type { CalendarEventType } from "~components/shared/event-calendar/helpers/big-calendar-helpers"
import { getIsWeekView } from "~components/shared/event-calendar/helpers/big-calendar-helpers"
import { QUERY_STRING_PROPS } from "~config/constants"
import {
    getUrlQueryParam,
    navigateToSearchParams,
} from "~utils/helpers/helpers"
import {
    getStartAndEndOfWeekEpochMs,
    getStartOfWeek,
} from "~utils/helpers/date-helpers"

const CalendarControlsContext =
    createContext<CalendarControlsProviderType>(getDefaults())

export function CalendarControlsProvider({ children }: PropsWithChildren) {
    const datesContext = useDatesContext()
    const {
        isOpen: isFilterModalOpen,
        onOpen: onFilterModalOpen,
        onClose: onFilterModalClose,
    } = useDisclosure()

    const context = useMemo(
        () => ({
            ...datesContext,
            isFilterModalOpen,
            onFilterModalOpen,
            onFilterModalClose,
        }),
        [datesContext, isFilterModalOpen, onFilterModalOpen, onFilterModalClose]
    )

    return (
        <CalendarControlsContext.Provider value={context}>
            {children}
        </CalendarControlsContext.Provider>
    )
}

export const useCalendarControlsContext = (): CalendarControlsProviderType => {
    return useContext(CalendarControlsContext)
}

// Context
function useDatesContext(): Omit<
    CalendarControlsProviderType,
    "isFilterModalOpen" | "onFilterModalOpen" | "onFilterModalClose"
> {
    const [selectedDates, setSelectedDates] = useState<[Date, Date]>([
        startOfDay(new Date()),
        endOfDay(new Date()),
    ])
    const [currentMonth, setCurrentMonth] = useState<Date>(
        getInitialCurrentMonthDate()
    )
    const [currentWeek, setCurrentWeek] = useState<Date>(getStartOfWeek())
    const [view, setView] = useState<View>(Views.MONTH)

    const isWeekView = getIsWeekView(view)

    // Query filters and period navigation depend on the active calendar view:
    // week view uses the selected week's epoch range; other views use the
    // current month's padded epoch range.
    const [startOfPeriodEpochMs, endOfPeriodEpochMs] = useMemo<
        [number, number]
    >(() => {
        if (isWeekView) return getStartAndEndOfWeekEpochMs(currentWeek)
        return getStartAndEndOfPeriodForCurrentMonthDate(currentMonth)
    }, [isWeekView, currentWeek, currentMonth])

    const goToMonth = (date: Date) => {
        const monthAnchor = startOfMonth(date)
        setCurrentMonth(monthAnchor)
        navigateToSearchParams({
            [QUERY_STRING_PROPS.CURRENT_MONTH]: monthAnchor
                .getTime()
                .toString(),
        })
        // Keep week view in sync so the picker also works when the user is
        // browsing weeks.
        if (isWeekView) {
            setCurrentWeek(getStartOfWeek(date))
        }
    }

    const goToWeek = (weekStart: Date) => {
        setCurrentWeek(weekStart)
        setCurrentMonth(startOfMonth(weekStart))
    }

    const incrementPeriod = (amount: number) => {
        if (isWeekView) {
            goToWeek(addWeeks(currentWeek, amount))
        } else {
            goToMonth(incrementMonth(currentMonth, amount))
        }
    }

    const goToToday = () => {
        if (isWeekView) {
            goToWeek(getStartOfWeek())
        } else {
            goToMonth(new Date())
        }
    }

    const setSelectedDatesFromEvent = (event: CalendarEventType) => {
        setSelectedDates([
            startOfDay(event.start_date),
            endOfDay(event.end_date),
        ])
    }

    return {
        selectedDates,
        selectedDatesEpochMs: getSelectedDatesEpochMs(selectedDates),
        setSelectedDates,
        setSelectedDatesFromEvent,
        currentMonth,
        currentWeek,
        isWeekView,
        setCurrentWeek,
        view,
        setView,
        startOfPeriodEpochMs,
        endOfPeriodEpochMs,
        goToNextPeriod: () => incrementPeriod(1),
        goToPreviousPeriod: () => incrementPeriod(-1),
        goToToday,
        goToMonth,
    }
}

// Helpers

export function getStartAndEndOfPeriodForCurrentMonthDate(
    currentMonth: Date
): [number, number] {
    // The calendar show a few days before and after the actual month
    // So we add a week on each side
    const startOfMonthEpochMs = addWeekAndGetTime(
        startOfMonth(currentMonth),
        -1
    )
    const endOfMonthEpochMs = addWeekAndGetTime(endOfMonth(currentMonth), 1)

    return [startOfMonthEpochMs, endOfMonthEpochMs]
}

function addWeekAndGetTime(date: Date, increment: number) {
    return addWeeks(date, increment).getTime()
}

// This simplifies the math of adding or removing a month
// addMonths was making this more complex due to months
// having different lengths
function incrementMonth(date: Date, increment: number): Date {
    // 0: Jan, 11: Dec
    const month = getMonth(date)
    const year = getYear(date)
    let newMonth = month + increment
    let newYear = year
    if (newMonth > 11) {
        newMonth = 0
        newYear = year + 1
    }
    if (newMonth < 0) {
        newMonth = 11
        newYear = year - 1
    }

    return set(date, {
        year: newYear,
        month: newMonth,
        date: 1,
        hours: 0,
        minutes: 0,
        seconds: 0,
    })
}

function getSelectedDatesEpochMs(
    selectedDates: [Date, Date]
): [number, number] {
    const now = new Date()
    const nowEpochMs = now.getTime()
    const timestamps = selectedDates.map((date) => date.getTime())

    return [timestamps[0] ?? nowEpochMs, timestamps[1] ?? nowEpochMs]
}

// Exported for testing.
export function getInitialCurrentMonthDate() {
    const currentMonth = getUrlQueryParam(QUERY_STRING_PROPS.CURRENT_MONTH)
    const queryParamDate = currentMonth ? new Date(Number(currentMonth)) : null

    if (queryParamDate && isValid(queryParamDate)) {
        return startOfMonth(queryParamDate)
    }

    return startOfMonth(new Date())
}

function getDefaults(): CalendarControlsProviderType {
    const now = new Date()
    const nowEpochMs = now.getTime()
    const weekStart = getStartOfWeek(now)
    const monthAnchor = getInitialCurrentMonthDate()
    const [startOfPeriodEpochMs, endOfPeriodEpochMs] =
        getStartAndEndOfPeriodForCurrentMonthDate(monthAnchor)

    return {
        // Dates
        selectedDates: [now, now],
        selectedDatesEpochMs: [nowEpochMs, nowEpochMs],
        setSelectedDates: () => {},
        setSelectedDatesFromEvent: () => {},
        currentMonth: monthAnchor,
        currentWeek: weekStart,
        isWeekView: false,
        setCurrentWeek: () => {},
        view: Views.MONTH,
        setView: () => {},
        startOfPeriodEpochMs,
        endOfPeriodEpochMs,
        goToNextPeriod: () => {},
        goToPreviousPeriod: () => {},
        goToToday: () => {},
        goToMonth: () => {},
        isFilterModalOpen: false,
        onFilterModalOpen: () => {},
        onFilterModalClose: () => {},
    }
}

export type CalendarControlsProviderType = {
    selectedDates: [Date, Date]
    selectedDatesEpochMs: [number, number]
    setSelectedDates: (dates: [Date, Date]) => void
    setSelectedDatesFromEvent: (event: CalendarEventType) => void
    currentMonth: Date
    currentWeek: Date
    isWeekView: boolean
    setCurrentWeek: (week: Date) => void
    view: View
    setView: (view: View) => void
    startOfPeriodEpochMs: number
    endOfPeriodEpochMs: number
    goToNextPeriod: () => void
    goToPreviousPeriod: () => void
    goToToday: () => void
    goToMonth: (date: Date) => void
    isFilterModalOpen: boolean
    onFilterModalOpen: () => void
    onFilterModalClose: () => void
}
